import { useState } from "react";
import { toast } from "sonner";
import { MapPin, PackageSearch, Thermometer, Truck, ArrowRight, ArrowLeft, Check, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { TRUCK_TYPES, MATERIAL_TYPES } from "@/lib/constants";

const STEPS = ["Route", "Cargo", "Temperature", "Truck & date"];
const ICONS = [MapPin, PackageSearch, Thermometer, Truck];
const BLANK = { origin: "", destination: "", material_type: "", weight: "", temp_min: "-18", temp_max: "4", truck_type: "", pickup_date: "", notes: "" };

export default function GuidedPostDialog({ open, onOpenChange, onCreated }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(BLANK);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid = () => {
    if (step === 0) return form.origin.trim() && form.destination.trim();
    if (step === 1) return form.material_type && form.weight;
    if (step === 2) return form.temp_min !== "" && form.temp_max !== "";
    if (step === 3) return form.truck_type && form.pickup_date;
    return false;
  };
  const next = () => { if (!valid()) { toast.error("Please fill this step to continue."); return; } setStep((s) => Math.min(s + 1, 3)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!valid()) { toast.error("Please complete this step."); return; }
    setSaving(true);
    try {
      await api.post("/loads", { ...form, weight: parseFloat(form.weight), temp_min: parseFloat(form.temp_min), temp_max: parseFloat(form.temp_max) });
      try { localStorage.setItem("rs_guided_done", "1"); } catch (e) { console.error(e); }
      toast.success("Your first load is live on the board!");
      onOpenChange(false);
      setStep(0);
      setForm(BLANK);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post load");
    } finally { setSaving(false); }
  };

  const Icon = ICONS[step];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Post your first load</DialogTitle>
          <DialogDescription>Step {step + 1} of 4 — {STEPS[step]}</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1.5 mb-1">
          {STEPS.map((label, i) => <div key={label} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-secondary"}`} />)}
        </div>

        <div className="py-2 space-y-4 min-h-[160px]">
          <div className="flex items-center gap-2 text-primary"><Icon className="w-4 h-4" /><span className="text-sm font-semibold">{STEPS[step]}</span></div>
          {step === 0 && (<>
            <div className="space-y-1.5"><Label>Origin (from)</Label><Input data-testid="guided-origin" value={form.origin} onChange={(e) => set("origin", e.target.value)} placeholder="e.g. Pune" /></div>
            <div className="space-y-1.5"><Label>Destination (to)</Label><Input data-testid="guided-destination" value={form.destination} onChange={(e) => set("destination", e.target.value)} placeholder="e.g. Delhi" /></div>
          </>)}
          {step === 1 && (<>
            <div className="space-y-1.5"><Label>What are you shipping?</Label><Select value={form.material_type} onValueChange={(v) => set("material_type", v)}><SelectTrigger data-testid="guided-material"><SelectValue placeholder="Select cargo type" /></SelectTrigger><SelectContent>{MATERIAL_TYPES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Weight (tonnes)</Label><Input data-testid="guided-weight" type="number" value={form.weight} onChange={(e) => set("weight", e.target.value)} placeholder="e.g. 9" /></div>
          </>)}
          {step === 2 && (<>
            <p className="text-xs text-muted-foreground">Set the temperature your cargo must stay within.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Min (°C)</Label><Input data-testid="guided-tempmin" type="number" value={form.temp_min} onChange={(e) => set("temp_min", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Max (°C)</Label><Input data-testid="guided-tempmax" type="number" value={form.temp_max} onChange={(e) => set("temp_max", e.target.value)} /></div>
            </div>
          </>)}
          {step === 3 && (<>
            <div className="space-y-1.5"><Label>Truck type needed</Label><Select value={form.truck_type} onValueChange={(v) => set("truck_type", v)}><SelectTrigger data-testid="guided-trucktype"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{TRUCK_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Pickup date</Label><Input data-testid="guided-date" type="date" value={form.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} /></div>
          </>)}
        </div>

        <div className="flex items-center justify-between gap-2">
          <Button data-testid="guided-back" variant="ghost" onClick={back} disabled={step === 0} className="rounded-full"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          {step < 3 ? (
            <Button data-testid="guided-next" onClick={next} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">Next <ArrowRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button data-testid="guided-submit" onClick={submit} disabled={saving} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"><Check className="w-4 h-4 mr-1" /> {saving ? "Posting…" : "Post my first load"}</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
